// interview-retire-ticket-questions.ts — phase 06h R1 (debate B1, N10) + 06i R1.
//
// F-40 holds a bootstrap ticket on its OWN pending `needs_decision` question
// (interview-required-answers-gate.ts). An operator who redirects/amends the
// ticket instead of answering must not be locked forever by a question nobody
// asks any more. For every PENDING question of a target ticket X:
//   - 06i R1 (F-45): the amend patch names exactly one option id (redirect-
//     amend-option-inference.ts) ⇒ the redirect ANSWERS the question — journal
//     `decision_answered` actor `redirect:<directive_id>`, interview answer with
//     the same `answered_by`, question kept — so the next attempt's prompt and
//     the decision ledger carry the decision (D8: lost, worker asked again).
//   - otherwise ⇒ retire as 06h: journal `decision_question_retired` with an enum
//     `reason` (no free text — JOURNAL_DELTA_KINDS_WITHOUT_FREE_TEXT), delete it.
// Other tickets' questions and answered ones stay.
//
// Target tickets come from the directive PAYLOAD, never from
// applyRedirectDirective's `touchedTicketIds` (empty on crash-then-replay).
// Replay: a `decision_answered` already journaled for (question, directive) is
// not written twice; the interview answer is still recorded when missing (crash
// between journal and interview write — review 06h L2 order).
import { randomUUID } from "node:crypto";
import {
  decisionAnsweredDelta,
  decisionAnswerKey,
  readJournaledDecisionAnswerKeys,
} from "./decision-answered-journal-event.js";
import type { RedirectDirectivePayload } from "./directive-redirect-application.js";
import {
  readInterview,
  recordInterviewAnswer,
  writeInterviewQuestions,
} from "./interview-store.js";
import type { MissionStore } from "./mission-store.js";
import { isNeedsDecisionQuestionOfTicket } from "./needs-decision-question-ticket-match.js";
import { inferAmendOptionId } from "./redirect-amend-option-inference.js";
import type { InterviewQuestion } from "./schemas/interview-schema.js";

export interface RetirePendingNeedsDecisionQuestionsInput {
  store: MissionStore;
  controlRoot: string;
  payload: RedirectDirectivePayload;
  directiveId: string;
  now?: () => Date;
}

/** Every ticket a redirect payload touches: amend/supersede/cancel targets, created ids, superseded done ids. */
export function redirectTargetTicketIds(payload: RedirectDirectivePayload): string[] {
  const ids = new Set<string>();
  for (const change of payload.proposed_changes ?? []) {
    const id = change.op === "create" ? change.new_ticket?.id : change.ticket_id;
    if (id !== undefined) ids.add(id);
  }
  for (const id of payload.supersede_done_ticket_ids ?? []) ids.add(id);
  return [...ids];
}

interface PendingQuestion {
  question: InterviewQuestion;
  ticket: string;
}

/**
 * Answers (amend names one option id) or retires the pending needs_decision
 * questions of the redirect's target tickets; returns the RETIRED question ids.
 */
export async function retirePendingNeedsDecisionQuestions(
  input: RetirePendingNeedsDecisionQuestionsInput,
): Promise<string[]> {
  const ticketIds = redirectTargetTicketIds(input.payload);
  const interview = await readInterview(input.controlRoot);
  if (interview === undefined || ticketIds.length === 0) return [];
  const pending: PendingQuestion[] = [];
  for (const question of interview.questions) {
    if (interview.answers[question.id] !== undefined) continue;
    const ticket = ticketIds.find((t) => isNeedsDecisionQuestionOfTicket(question, t));
    if (ticket !== undefined) pending.push({ question, ticket });
  }
  if (pending.length === 0) return [];

  const actor = `redirect:${input.directiveId}`;
  const at = (input.now ?? (() => new Date()))().toISOString(); // N2: decided_at === answered_at
  const answered: Array<PendingQuestion & { optionId: string }> = [];
  const retired: Array<PendingQuestion & { reason: string }> = [];
  for (const entry of pending) {
    const inferred = inferAmendOptionId(input.payload, entry.ticket, entry.question);
    if (inferred.ok) answered.push({ ...entry, optionId: inferred.optionId });
    else retired.push({ ...entry, reason: inferred.reason });
  }

  // Review 06h L2: journal FIRST, interview second — a crash between the two
  // leaves the audit record and the replay finishes the interview write.
  const journaled =
    answered.length === 0
      ? new Set<string>()
      : await readJournaledDecisionAnswerKeys(input.controlRoot);
  const deltas = [
    ...answered
      .filter((a) => !journaled.has(decisionAnswerKey(a.question.id, input.directiveId)))
      .map((a) => decisionAnsweredDelta(a.question, a.optionId, actor, input.directiveId, at)),
    ...retired.map((r) => ({
      kind: "decision_question_retired" as const,
      question_id: r.question.id,
      ticket: r.ticket,
      directive_id: input.directiveId,
      reason: r.reason,
    })),
  ];
  if (deltas.length > 0) {
    await input.store.commit(deltas.map((delta) => ({ event_id: randomUUID(), delta })));
  }
  for (const a of answered) {
    const recorded = await recordInterviewAnswer({
      controlRoot: input.controlRoot,
      questionId: a.question.id,
      answer: a.optionId,
      answeredBy: actor,
      now: () => new Date(at),
    });
    // Review 06i M2: fail loud — a journaled decision without its interview answer
    // would leave the gate blocked while the ledger says "decided"; the next
    // `run` replays and finishes the write.
    if (!recorded.ok) {
      throw new Error(`06i: không ghi được answer ${a.question.id}: ${recorded.reason}`);
    }
  }
  if (retired.length === 0) return [];
  const retiredIds = new Set(retired.map((r) => r.question.id));
  await writeInterviewQuestions({
    controlRoot: input.controlRoot,
    questions: interview.questions.filter((q) => !retiredIds.has(q.id)),
  });
  return [...retiredIds];
}
